import React, { useState } from 'react';
import { WrenchScrewdriverIcon, CalendarIcon, BellAlertIcon, PlusIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import CommonPageHeader from '../components/common/CommonPageHeader';

const FleetMaintenance: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState<'upcoming' | 'history' | 'schedule'>('upcoming');

  // Mock adatok
  const upcomingServices = [
    { id: 1, vehicle: 'ABC-123', type: 'Olajcsere', date: '2024. 02. 15.', km: 15000, status: 'scheduled' },
    { id: 2, vehicle: 'DEF-456', type: 'Műszaki vizsga', date: '2024. 02. 20.', km: null, status: 'urgent' },
    { id: 3, vehicle: 'GHI-789', type: 'Gumiabroncs csere', date: '2024. 03. 01.', km: 20000, status: 'scheduled' },
  ];

  const serviceHistory = [
    { id: 1, vehicle: 'ABC-123', type: 'Rendszeres karbantartás', date: '2024. 01. 15.', cost: 45000, provider: 'AutoSzerviz Kft.' },
    { id: 2, vehicle: 'DEF-456', type: 'Fékbetét csere', date: '2024. 01. 10.', cost: 32000, provider: 'Gyorsszerviz' },
    { id: 3, vehicle: 'GHI-789', type: 'Olajcsere + szűrő', date: '2024. 01. 05.', cost: 28000, provider: 'AutoSzerviz Kft.' },
  ];

  return (
    <div className="space-y-6">
      <CommonPageHeader
        title="Karbantartás & Szerviz"
        subtitle="Szerviz előzmények, karbantartási ütemterv és emlékeztetők"
        icon={WrenchScrewdriverIcon}
      />

      {/* Flotta modul overlay */}
      <div className="relative">
        <div className="absolute inset-0 bg-gray-900/5 dark:bg-gray-900/20 backdrop-blur-[0.5px] z-10 rounded-lg flex items-center justify-center">
          <div className="bg-white/95 dark:bg-gray-800/95 px-8 py-6 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-md text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-primary-400 to-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🚗</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Flotta Modul</h3>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
              Ez a funkció a Flotta modul részét képezi
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              A Karbantartás & Szerviz modul külön megrendelés tárgya.
            </p>
          </div>
        </div>

        {/* Teljes működő tartalom (elmosódva) */}
        <div className="pointer-events-none opacity-70 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <CalendarIcon className="w-8 h-8 text-blue-600 mb-2" />
              <h3 className="font-semibold mb-1">Következő szervizek</h3>
              <p className="text-2xl font-bold">12</p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <WrenchScrewdriverIcon className="w-8 h-8 text-green-600 mb-2" />
              <h3 className="font-semibold mb-1">Elvégzett szervizek</h3>
              <p className="text-2xl font-bold">48</p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
              <BellAlertIcon className="w-8 h-8 text-red-600 mb-2" />
              <h3 className="font-semibold mb-1">Lejáró emlékeztetők</h3>
              <p className="text-2xl font-bold">3</p>
            </div>
          </div>

          {/* Új szerviz gomb */}
          <div className="flex justify-end">
            <button className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-md">
              <PlusIcon className="w-5 h-5" />
              Új szerviz rögzítése
            </button>
          </div>

          {/* Tabok */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="border-b border-gray-200 dark:border-gray-700">
              <div className="flex gap-4 px-6">
                <button
                  onClick={() => setSelectedTab('upcoming')}
                  className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                    selectedTab === 'upcoming'
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Következő szervizek
                </button>
                <button
                  onClick={() => setSelectedTab('history')}
                  className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                    selectedTab === 'history'
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Előzmények
                </button>
                <button
                  onClick={() => setSelectedTab('schedule')}
                  className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                    selectedTab === 'schedule'
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Ütemterv
                </button>
              </div>
            </div>

            <div className="p-6">
              {selectedTab === 'upcoming' && (
                <div className="space-y-4">
                  {upcomingServices.map((service) => (
                    <div key={service.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-bold text-lg">{service.vehicle}</span>
                            <span className={`px-2 py-1 text-xs font-semibold rounded ${
                              service.status === 'urgent'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {service.status === 'urgent' ? 'Sürgős' : 'Ütemezett'}
                            </span>
                          </div>
                          <p className="text-gray-900 dark:text-white font-medium">{service.type}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
                            <span>📅 {service.date}</span>
                            {service.km && <span>🛣️ {service.km.toLocaleString()} km</span>}
                          </div>
                        </div>
                        <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm">
                          Elvégezve
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {selectedTab === 'history' && (
                <div className="space-y-4">
                  {serviceHistory.map((service) => (
                    <div key={service.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-bold text-lg">{service.vehicle}</span>
                            <CheckCircleIcon className="w-5 h-5 text-green-600" />
                          </div>
                          <p className="text-gray-900 dark:text-white font-medium">{service.type}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
                            <span>📅 {service.date}</span>
                            <span>🏢 {service.provider}</span>
                            <span className="font-semibold text-gray-900 dark:text-white">💰 {service.cost.toLocaleString()} Ft</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {selectedTab === 'schedule' && (
                <div className="text-center py-12">
                  <CalendarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">Karbantartási ütemterv nézet</p>
                  <p className="text-sm text-gray-500 mt-2">Havi/éves naptár a tervezett szervizekkel</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FleetMaintenance;
